import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { LuMaximize2, LuSquarePen, LuTarget, LuX } from 'react-icons/lu';
import aviso, { textoDoErro } from '../lib/aviso';
import AssistenteComposer from './AssistenteComposer';
import AssistenteMensagem from './AssistenteMensagem';
import AssistentePensando, { type ConsultaDoBrik } from './AssistentePensando';
import AssistenteService, {
    avisarAcaoDoAssistente,
    avisarConversasMudaram,
    type AcaoAssistente,
    type MensagemAssistente,
} from '../services/AssistenteService';
import { useAgente } from '../contexts/AgenteContext';
import { rotaSemPortaDoAgente } from './AgentePorta';
import BrikAgente from './BrikAgente';
import '../pages/AssistenteLayout.css';
import './AgentePainel.css';

/* A conversa do painel sobrevive a fechar e reabrir na mesma tela e a
   recarregar a aba — mas não a fechar o navegador, que é quando "continuar de
   onde parou" deixa de ser o esperado. Por isso fica guardada também a tela em
   que ela está: reabrir o painel noutra tela começa conversa nova.
   `sessionStorage` pode não existir (aba privada, bloqueio): sem ele o painel
   só começa uma conversa nova. */
const CHAVE_CONVERSA = 'lemeia:agente-conversa';
const CHAVE_ROTA = 'lemeia:agente-conversa-rota';
const lerConversaGuardada = (): number | null => {
    try { const v = Number(sessionStorage.getItem(CHAVE_CONVERSA)); return v > 0 ? v : null; } catch { return null; }
};
const lerRotaGuardada = (): string | null => {
    try { return sessionStorage.getItem(CHAVE_ROTA); } catch { return null; }
};
const guardarConversa = (id: number | null, rota?: string) => {
    try {
        if (id) {
            sessionStorage.setItem(CHAVE_CONVERSA, String(id));
            if (rota) sessionStorage.setItem(CHAVE_ROTA, rota);
        } else {
            sessionStorage.removeItem(CHAVE_CONVERSA);
            sessionStorage.removeItem(CHAVE_ROTA);
        }
    } catch { /* sem armazenamento */ }
};

const idLocal = () => -Date.now();

/**
 * O agente ao lado da tela: o painel que a porta do cabeçalho abre, em todas as
 * telas menos a Home e a conversa em tela cheia.
 *
 * A conversa é uma conversa comum do assistente — aparece no histórico da
 * sidebar e abre em tela cheia pelo botão do topo. O que muda é o contexto: se
 * a tela registrou um registro (`useRegistroDoAgente`), a pergunta leva o id e
 * "ganhei essa" sabe de qual oportunidade se fala. E toda pergunta leva o
 * endereço da tela de AGORA (a conversa atravessa telas), para "o que posso
 * fazer aqui?" saber onde é o aqui.
 */
const AgentePainel: FC = () => {
    const agente = useAgente();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const [conversaId, setConversaId] = useState<number | null>(lerConversaGuardada);
    const [mensagens, setMensagens] = useState<MensagemAssistente[]>([]);
    const [carregada, setCarregada] = useState<number | null>(null);
    const [perguntando, setPerguntando] = useState(false);
    /* O texto da resposta enquanto o modelo escreve (PerguntarStream). */
    const [parcial, setParcial] = useState('');
    /* O que o Brik está consultando agora (as frases do AssistentePensando). */
    const [consulta, setConsulta] = useState<ConsultaDoBrik | null>(null);
    const [degradadas, setDegradadas] = useState<Set<number>>(new Set());
    const refFim = useRef<HTMLDivElement>(null);
    /* O pulinho de "feito" no topo: sobe a cada ação que termina (marca, seção 3). */
    const [feitoEm, setFeitoEm] = useState(0);
    useEffect(() => {
        if (!feitoEm) return;
        const t = setTimeout(() => setFeitoEm(0), 1000);
        return () => clearTimeout(t);
    }, [feitoEm]);

    const aberto = !!agente?.aberto && !rotaSemPortaDoAgente(pathname);

    /* O painel desliza da direita ao abrir e volta deslizando ao fechar
       (pedido dele em 06/10). Para a saída ser vista, ele continua montado
       pelos 200 ms da animação depois de fechado. */
    const [saindo, setSaindo] = useState(false);
    const estavaAberto = useRef(aberto);
    useEffect(() => {
        if (estavaAberto.current && !aberto) {
            setSaindo(true);
            const t = setTimeout(() => setSaindo(false), 200);
            estavaAberto.current = aberto;
            return () => clearTimeout(t);
        }
        if (aberto) setSaindo(false);
        estavaAberto.current = aberto;
    }, [aberto]);
    const registro = agente?.registro ?? null;
    const fechar = agente?.fechar;

    /* Trocar de tela e abrir o painel de novo começa conversa nova (pedido dele
       em 11/10): o assunto é a tela de agora, não a de onde ele veio. A conversa
       anterior não se perde — fica no histórico do assistente. Com o painel
       ABERTO a navegação não corta nada: a pessoa continua a mesma conversa
       enquanto anda pelo sistema, inclusive quando é o próprio Brik que a leva
       para um registro. */
    const rotaDaConversa = useRef<string | null>(lerRotaGuardada());
    const abertoAntes = useRef(aberto);
    if (aberto !== abertoAntes.current) {
        abertoAntes.current = aberto;
        if (aberto && rotaDaConversa.current && rotaDaConversa.current !== pathname) {
            rotaDaConversa.current = pathname;
            setConversaId(null);
            setCarregada(null);
            setMensagens([]);
            setDegradadas(new Set());
        }
    }

    /* O que fica guardado é o par conversa + tela, e a tela acompanha a
       navegação enquanto o painel está aberto. */
    useEffect(() => {
        if (!aberto) return;
        rotaDaConversa.current = pathname;
        guardarConversa(conversaId, pathname);
    }, [aberto, pathname, conversaId]);

    /* Abriu com uma conversa guardada que ainda não está em memória: carrega. */
    useEffect(() => {
        if (!aberto || !conversaId || carregada === conversaId) return;
        let vivo = true;
        AssistenteService.buscarConversa(conversaId)
            .then(dados => { if (vivo) { setMensagens(dados.mensagens); setCarregada(conversaId); } })
            .catch(() => { if (vivo) { setConversaId(null); setMensagens([]); } });
        return () => { vivo = false; };
    }, [aberto, conversaId, carregada]);

    /* Clique fora fecha (pedido dele em 06/10). Não conta clique no próprio
       botão do Brik (ele já alterna), no balão, nem dentro de diálogo, menu
       suspenso ou aviso que esteja por cima — esses vivem fora do painel por
       portal, mas são parte do que a pessoa está fazendo nele. */
    useEffect(() => {
        if (!aberto || !fechar) return;
        const aoClicar = (e: MouseEvent) => {
            const alvo = e.target as HTMLElement | null;
            if (!alvo?.closest) return;
            if (alvo.closest('.agt, .agt-porta, .agt-porta-celular, .mrc-balao, [role="dialog"], [role="listbox"], [role="menu"], [aria-live]')) return;
            fechar();
        };
        window.addEventListener('mousedown', aoClicar);
        return () => window.removeEventListener('mousedown', aoClicar);
    }, [aberto, fechar]);

    /* Esc fecha, como o RecordPanel. */
    useEffect(() => {
        if (!aberto || !fechar) return;
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar(); };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [aberto, fechar]);

    useEffect(() => {
        refFim.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [mensagens.length, perguntando, aberto, parcial]);

    const perguntar = useCallback(async (texto: string) => {
        const pendente: MensagemAssistente = {
            mensagemId: idLocal(), papel: 'user', conteudo: texto, fontes: [], criadaEm: new Date().toISOString(),
        };
        setMensagens(ms => [...ms, pendente]);
        setParcial('');
        setConsulta(null);
        setPerguntando(true);

        try {
            const dados = await AssistenteService.perguntar(
                texto, conversaId ?? undefined, undefined, 'digitada',
                registro ? { tipo: registro.tipo, id: registro.id, rota: pathname } : { rota: pathname },
                setParcial,
                ferramentas => setConsulta(c => ({ ferramentas, vez: (c?.vez ?? 0) + 1 })),
            );
            setConversaId(dados.conversa.conversaId);
            setCarregada(dados.conversa.conversaId);
            setMensagens(ms => [...ms.filter(m => m.mensagemId !== pendente.mensagemId), dados.pergunta, dados.resposta]);
            if (dados.degradada) setDegradadas(d => new Set(d).add(dados.resposta.mensagemId));

            /* Ação verde já sai feita: a tela por trás precisa recarregar. */
            const feitas = (dados.resposta.acoes ?? []).filter(a => a.estado === 'feita');
            feitas.forEach(avisarAcaoDoAssistente);
            if (feitas.length > 0) setFeitoEm(Date.now());
            avisarConversasMudaram();
        } catch (erro) {
            setMensagens(ms => ms.filter(m => m.mensagemId !== pendente.mensagemId));
            aviso.erro(textoDoErro(erro, 'Não foi possível responder agora.'));
        } finally {
            setParcial('');
            setConsulta(null);
            setPerguntando(false);
        }
    }, [conversaId, registro, pathname]);

    const avaliar = async (mensagem: MensagemAssistente, valor: 1 | -1) => {
        const anterior = mensagem.avaliacao ?? null;
        const nova = anterior === valor ? null : valor;
        const trocar = (v: 1 | -1 | null) => setMensagens(ms =>
            ms.map(m => (m.mensagemId === mensagem.mensagemId ? { ...m, avaliacao: v } : m)));
        trocar(nova);
        try { await AssistenteService.avaliar(mensagem.mensagemId, nova); }
        catch { trocar(anterior); aviso.erro('Não foi possível registrar a avaliação.'); }
    };

    const trocarAcao = (mensagemId: number, nova: AcaoAssistente) => {
        if (nova.estado === 'feita') setFeitoEm(Date.now());
        setMensagens(ms => ms.map(m => (m.mensagemId !== mensagemId ? m : {
            ...m, acoes: (m.acoes ?? []).map(a => (a.acaoId === nova.acaoId ? nova : a)),
        })));
    };

    const novaConversa = () => {
        setConversaId(null); setCarregada(null); setMensagens([]); setDegradadas(new Set());
    };

    const telaCheia = () => {
        if (!conversaId) return;
        fechar?.();
        navigate(`/assistente/${conversaId}`);
    };

    const ultimaRespostaId = [...mensagens].reverse().find(m => m.papel === 'model')?.mensagemId;

    if (!agente || (!aberto && !saindo)) return null;

    return createPortal(
        <aside className={`agt as-screen ${saindo ? 'is-saindo' : ''}`} role="dialog" aria-label="Brik AI">
            {/* O topo é só o nome: o Brik mora ao lado de cada fala (pedido dele
                em 06/10), e é lá que ele pensa e comemora. */}
            <div className="agt-topo">
                <span className="agt-titulo">Brik AI</span>
                <span className="agt-topo-acoes">
                    <button type="button" className="agt-ib" onClick={novaConversa} disabled={perguntando || mensagens.length === 0}
                        aria-label="Nova conversa" title="Nova conversa">
                        <LuSquarePen size={16} />
                    </button>
                    <button type="button" className="agt-ib" onClick={telaCheia} disabled={!conversaId}
                        aria-label="Abrir em tela cheia" title="Abrir em tela cheia">
                        <LuMaximize2 size={16} />
                    </button>
                    <button type="button" className="agt-ib" onClick={fechar} aria-label="Fechar" title="Fechar">
                        <LuX size={16} />
                    </button>
                </span>
            </div>

            {registro && (
                <div className="agt-ctx">
                    <span className="agt-ctx-rot">Nesta tela</span>
                    <span className="agt-ctx-chip"><LuTarget size={14} /><span>{registro.rotulo}</span></span>
                </div>
            )}

            <div className="agt-msgs">
                {mensagens.length === 0 && !perguntando && (
                    <div className="agt-vazio">
                        <BrikAgente tamanho="grande" px={96} animar={false} />
                        <b>O que você quer fazer?</b>
                        Peça ou pergunte em uma frase — por exemplo, “anota que ele pediu desconto e me lembra sexta às 10h”.
                    </div>
                )}

                {mensagens.map(m => {
                    /* O Brik fala: ele no pé de cada resposta, do lado esquerdo. Só o
                       da última se mexe (pisca, comemora o "feito") — vários olhos
                       piscando pela conversa disputariam a atenção. */
                    const ultima = m.mensagemId === ultimaRespostaId && !perguntando;
                    return (
                        <AssistenteMensagem
                            key={m.mensagemId}
                            mensagem={m}
                            degradada={degradadas.has(m.mensagemId)}
                            onAvaliar={avaliar}
                            onAcaoMudou={trocarAcao}
                            onAbrirRegistro={fechar}
                            ultima={ultima}
                            avatar={
                                <span className="agt-fala-brik">
                                    <BrikAgente px={24} estado={ultima && feitoEm ? 'feito' : 'repouso'} animar={ultima} />
                                </span>
                            }
                        />
                    );
                })}

                {/* A resposta aparecendo enquanto é escrita: a mesma bolha das
                    outras, com o Brik ainda pensando. A gravada a substitui no fim. */}
                {perguntando && parcial && (
                    <AssistenteMensagem
                        mensagem={{ mensagemId: -1, papel: 'model', conteudo: parcial, fontes: [], criadaEm: new Date().toISOString() }}
                        avatar={
                            <span className="agt-fala-brik">
                                <BrikAgente px={24} estado="pensando" animar={false} />
                            </span>
                        }
                    />
                )}

                {perguntando && !parcial && (
                    <div className="as-msg is-model com-avatar">
                        <div className="as-bolha-linha">
                            <span className="agt-fala-brik">
                                <BrikAgente px={24} estado="pensando" animar={false} />
                            </span>
                            <AssistentePensando consulta={consulta} />
                        </div>
                    </div>
                )}
                <div ref={refFim} />
            </div>

            <div className="agt-doca">
                <AssistenteComposer onEnviar={perguntar} enviando={perguntando} autoFocus />
            </div>
        </aside>,
        document.body,
    );
};

export default AgentePainel;
